import { useEffect, useState } from 'react';
import { GetPeoplePerson } from '../../utils/CommonTypes/PeoplePageTypes';

import Icon, { IconType } from '../Icon/Icon';
import Modal from '../Modal/Modal';
import { toast } from 'react-toastify';
import Button from '../_Core/Button';
import './BulkEditsStyles.css';
import { IListItem } from '../FloorPlanner/FloorPlannerTypes';
import { store } from '../../redux/oldStore';
import { ServerResponse } from '../../utils/Server';
import { FormatDate } from '../../utils/Tools';

interface IProps {
  selected: Record<string, boolean>;
  refresh: () => void;

  people: GetPeoplePerson[];
  schools: IListItem[];
}

interface ISelectedPeopleInfo {
  allSelectedAreParticipants: boolean;
  allSelectedAreNonParticipants: boolean;
  numberOfSelected: number;
  selectedIds: string[];
}

interface IForm {
  schoolId?: number;
  teacherId?: number;
}

interface IMergePersonConflict {
  Type: string;
  Description: string;
  StartTime: string;
  EndTime?: string;
}

interface IMergePersonResult {
  Merged: boolean;
  Conflicts: IMergePersonConflict[];
}

// plural forms that aren't just "Role" + "s"
const ROLE_PLURALS: Record<string, string> = {
  Staff: 'Staff',
};

const pluralizeRole = (role: string) => ROLE_PLURALS[role] ?? `${role}s`;

const BulkEdits = (props: IProps) => {
  const [selectedStats, setSelectedStats] = useState<ISelectedPeopleInfo>();
  const [showMergePeople, setShowMergePeople] = useState<boolean>(false);
  const [mergeConflicts, setMergeConflicts] = useState<IMergePersonConflict[]>([]);
  const [peopleDic, setPeopleDic] = useState<Record<string, GetPeoplePerson>>({});
  const [selectedPeople, setSelectedPeople] = useState<GetPeoplePerson[]>([]);
  const [showBulkEditStudent, setShowBulkEditStudent] = useState<boolean>(false);
  const [teachers, setTeachers] = useState<IListItem[]>([]);
  const [form, setForm] = useState<IForm>({});

  const mergeRoleLabel =
    selectedPeople.length === 2 && selectedPeople[0].MaxRole && selectedPeople[0].MaxRole === selectedPeople[1].MaxRole
      ? pluralizeRole(selectedPeople[0].MaxRole)
      : 'Users';

  useEffect(() => {
    if (props.people) {
      let dic = {};

      props.people.forEach((x) => {
        dic[x.Id] = x;
      });

      setPeopleDic(dic);
    }
  }, [props.people]);

  useEffect(() => {
    let newTeachers = props.people
      .filter((x) => x.Adult && !x.TempPerson && x.MaxRole !== 'Judge')
      .map((x) => {
        return { Id: x.PersonId, Name: `${x.LastName}, ${x.FirstName} ${x.Email}` };
      });

    setTeachers(newTeachers);
  }, [props.people]);

  useEffect(() => {
    let info: ISelectedPeopleInfo = {
      allSelectedAreParticipants: true,
      allSelectedAreNonParticipants: true,
      numberOfSelected: 0,
      selectedIds: Object.keys(props.selected),
    };

    let selectedPeopleArray = props.people.filter((x) => props.selected[x.Id]);

    info.numberOfSelected = selectedPeopleArray.length;
    info.allSelectedAreNonParticipants = selectedPeopleArray.find((x) => x.Adult === false) ? false : true;
    info.allSelectedAreParticipants = selectedPeopleArray.find((x) => x.Adult === true) ? false : true;
    setSelectedPeople(selectedPeopleArray);
    setSelectedStats(info);
    setMergeConflicts([]);
  }, [props.selected, props.people]);

  const mergePeople = (force: boolean) => {

    toast.clearWaitingQueue();
    //tell server of change
    let request = {
      idA:selectedPeople[0].PersonId, //Account that will be removed. should/could be placeholder account...
      idB:selectedPeople[1].PersonId, //Account to keep
      force,
    };
    store.server.postApi<ServerResponse<IMergePersonResult>>(`../Person/MergePerson`,request)
    .then(x=>{
      if(x.Success)
        {
          if (x.Value.Merged) {
            //tell partent of change
            props.refresh();
            //clear saved data...
            setForm({});
            setMergeConflicts([]);
            setShowMergePeople(false);
            //done
            toast.dark(`${mergeRoleLabel} Merged...`);
          } else {
            //conflicts found - show them and let the admin confirm before merging anyway
            setMergeConflicts(x.Value.Conflicts);
          }
      }
      else {
        toast.error(x.Message);
      }
    });


  };

  const changeTeacher = () =>{

    toast.clearWaitingQueue();
    //tell server of change
    let request = {
      newId:form.teacherId,
      participants: selectedPeople.map(x=>{
        return {personId:x.PersonId, projectId: x.ProjectIdInt}
      })
    };
    store.server.postApi<ServerResponse<boolean>>(`../Person/ChangeTeacher`,request)
    .then(x=>{
      if(x.Success) 
        {//code to process once we tell the server...        
          //tell partent of change
          props.refresh();
          //clear saved data...
          setForm({});
          //done
          toast.dark('Changed teacher...');
      }
      else {
        toast.error(x.Message);
      }
    });

  };

  const changeSchool = ()=>{
    toast.clearWaitingQueue();
    //tell server of change
    let request = {
      newId:form.schoolId,
      participants: selectedPeople.map(x=>{
        return {personId:x.PersonId, projectId: x.ProjectIdInt}
      })
    };
    store.server.postApi<ServerResponse<boolean>>(`../Person/ChangeSchool`,request)
    .then(x=>{
      if(x.Success) 
        {//code to process once we tell the server...        
          //tell partent of change
          props.refresh();
          //clear saved data...
          setForm({});
          //done
          toast.dark('Changed School...');
      }
      else {
        toast.error(x.Message);
      }
    
  });
}

  return (
    <>
      {(selectedStats?.numberOfSelected ?? 0) > 1 && selectedStats?.allSelectedAreParticipants && (
        <button
          type="button"
          className="btn btn-default btn-xs"
          onClick={() => {
            setShowBulkEditStudent(true);
          }}
          title="Bulk Student Edit">
          <Icon type={IconType.magic} />
          Bulk Student Edit
        </button>
      )}

      {showBulkEditStudent && (
        <Modal setModalOpen={setShowBulkEditStudent} title={'Bulk Edit Student Accounts'} size="l">
          <div className="form-horizontal bulk-edit-teacher-school-container">
            <h3>Selected Participants</h3>
            <div className="flex-between">
              {selectedPeople.map((x, i) => {
                return (
                  <div className="bulk-edit-container-person">
                    <div key={i}>
                      {x.FirstName} {x.LastName}
                      <br />
                      {x.ProjectId}
                      <br />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="form-horizontal">
            <div className="bulk-edit-option-card">
              <h3>Change School</h3>
              <hr />
              <div className="form-group">
                <label className="control-label col-sm-4">School</label>
                <div className="col-sm-7">
                  <select
                    value={form.schoolId ?? ''}
                    onChange={(x) => {
                      setForm({ ...form, schoolId: +x.target.value });
                    }}
                    className="form-control">
                    <option value={''}></option>
                    {props.schools.map((x, i) => {
                      return (
                        <option key={i} value={x.Id}>
                          {x.Name}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <div className="col-sm-offset-4 col-sm-7">
                  <button type="button" className="btn btn-default" title="change School" onClick={changeSchool} disabled={(form.schoolId ?? 0) > 0 ? false:true}>
                    <Icon type={IconType.save} />
                    Change
                  </button>
                </div>
              </div>
            </div>
            <div className="bulk-edit-option-card">
              <h3>Change Teacher</h3>
              <hr />
              <div className="form-group">
                <label className="control-label col-sm-4">Teacher</label>
                <div className="col-sm-7">
                  <select
                    value={form.teacherId ?? ''}
                    onChange={(x) => {
                      setForm({ ...form, teacherId: +x.target.value });
                    }}
                    className="form-control">
                    <option value={''}></option>
                    {teachers.map((x, i) => {
                      return (
                        <option key={i} value={x.Id}>
                          {x.Name}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <div className="col-sm-offset-4 col-sm-7">
                  <button 
                    type="button" 
                    className="btn btn-default" 
                    title="change Teacher" 
                    disabled={(form.teacherId ?? 0) > 0 ? false:true}
                    onClick={changeTeacher}
                  >
                    <Icon type={IconType.save} />
                    Change
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {selectedStats?.numberOfSelected === 2 && selectedStats.allSelectedAreNonParticipants && (
        <button
          type="button"
          className="btn btn-default btn-xs"
          onClick={() => {
            setMergeConflicts([]);
            setShowMergePeople(true);
          }}
          title="Merge Adults">
          <Icon type={IconType.magic} />
          Merge {mergeRoleLabel}
        </button>
      )}

      {showMergePeople && (
        <Modal setModalOpen={setShowMergePeople} title={`Merge ${mergeRoleLabel} Accounts`} size="l">
          <div className="form-horizontal">
            <div className="bulk-merge-container flex-between">
              {selectedPeople.length === 2 && (
                <>
                  <div className="bulk-merg-container-person">
                    <h3 className="bg-warning bulk-merg-container-header">Account to Merge</h3>
                    {selectedPeople[0].FirstName} {selectedPeople[0].LastName}
                    <br />
                    {selectedPeople[0].Email}
                    {selectedPeople[0].IsPlaceHolder && (
                      <>
                        <br />
                        <span className="label label-warning">placeholder</span>
                      </>
                    )}
                  </div>
                  <div>
                    {' '}
                    <button
                      type="button"
                      className="btn btn-default"
                      onClick={() => {
                        let value = [...selectedPeople.reverse()];
                        setSelectedPeople(value);
                        setMergeConflicts([]);
                      }}>
                      <Icon type={IconType.swap} /> Swap
                    </button>
                  </div>
                  <div className="bulk-merg-container-person">
                    <h3 className="bg-success bulk-merg-container-header">Account to Keep</h3>
                    {selectedPeople[1].FirstName} {selectedPeople[1].LastName}
                    <br />
                    {selectedPeople[1].Email}
                    {selectedPeople[1].IsPlaceHolder && (
                      <>
                        <br />
                        <span className="label label-warning">placeholder</span>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
            {mergeConflicts.length > 0 && (
              <div className="alert alert-warning">
                <strong>These accounts have overlapping assignments:</strong>
                <ul>
                  {mergeConflicts.map((c, i) => (
                    <li key={i}>
                      {c.Description} ({FormatDate(c.StartTime, 'date-time')}
                      {c.EndTime ? ` – ${FormatDate(c.EndTime, 'date-time')}` : ''})
                    </li>
                  ))}
                </ul>
                Merging will move both sets of assignments onto the kept account, leaving it double-booked. Review before proceeding.
              </div>
            )}
            <div className="merge-button">
              <button type="button" onClick={() => mergePeople(mergeConflicts.length > 0)} className="btn btn-secondary ">
                {mergeConflicts.length > 0 ? 'Merge Anyway' : 'Merge'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default BulkEdits;
